import { useCallback, useState } from 'react';
import type { DocumentFile, DocumentFolder } from '../../types';

export type SelectableItem = DocumentFile | DocumentFolder;

export interface UseMultiselectReturn {
  /** All currently selected items as an ordered array. */
  selectedItems: SelectableItem[];
  /** Returns true when the item with the given id is selected. */
  isSelected: (id: string) => boolean;
  /** Adds the item if not selected; removes it if already selected. */
  toggleItem: (item: SelectableItem) => void;
  /** Removes all selections. */
  clearSelection: () => void;
  /** True when at least one item is selected. */
  hasSelection: boolean;
}

/**
 * Manages multiselect state for files and folders in the document explorer.
 * Selection is keyed by item id. Order of insertion is preserved.
 */
export function useMultiselect(): UseMultiselectReturn {
  // Map preserves insertion order and gives O(1) id lookups
  const [selectionMap, setSelectionMap] = useState<Map<string, SelectableItem>>(new Map());

  const isSelected = useCallback(
    (id: string) => selectionMap.has(id),
    [selectionMap]
  );

  const toggleItem = useCallback((item: SelectableItem) => {
    setSelectionMap((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.set(item.id, item);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectionMap(new Map());
  }, []);

  return {
    selectedItems: Array.from(selectionMap.values()),
    isSelected,
    toggleItem,
    clearSelection,
    hasSelection: selectionMap.size > 0,
  };
}
